/**
 *  Control de costos (costos/*) → /api/v1/costs
 *
 *  Koa regresaba las filas del SP directo (arreglos, no {data}), con algunas
 *  transformaciones (materiales → {materials, tools}, JSON parseado en
 *  órdenes y tareas). Aquí se arma esa misma forma a partir de v1.
 *
 *  Varios PUT viejos mandan los datos en el query string
 *  (costos/x?id=…&name=…); se leen de ctx.url.
 */
import api from '../apiV1';
import { fail, done } from './index';

// --- utilidades ---

// query lee el query string de la url original; "undefined"/"null"/"" se
// vuelven undefined (URLSearchParams los manda como texto).
function query(url) {
   const out = {};
   const qs = (url || '').split('?')[1] || '';
   new URLSearchParams(qs).forEach((v, k) => {
      out[k] = v === 'undefined' || v === 'null' || v === '' ? undefined : v;
   });
   return out;
}

// parse acepta un JSON en texto (como lo mandaba la web a Koa) o un arreglo.
function parse(v, def = []) {
   if (v === undefined || v === null || v === '') return def;
   if (typeof v !== 'string') return v;
   try {
      const r = JSON.parse(v);
      return r === null ? def : r;
   } catch (e) {
      return def;
   }
}

const num = (v, def = 0) => {
   const n = Number(v);
   return v === undefined || v === null || v === '' || Number.isNaN(n) ? def : n;
};

// id de un objeto seleccionado ({id, name}) o de un número suelto.
const idOf = (v) => (v && typeof v === 'object' ? num(v.id) : num(v));

// Estados: 1 activo, 3 inactivo (2 es la baja y nunca llega).
const statusId = (active) => (active ? 1 : 3);
const statusName = (active) => (active ? 'Activo' : 'Inactivo');
const isActive = (idStatus, def = true) => (idStatus === undefined || idStatus === null ? def : Number(idStatus) === 1);

// Fechas: v1 usa YYYY-MM-DD; las pantallas de costos, dd/mm/yyyy.
function toDMY(v) {
   if (!v) return null;
   const [y, m, d] = String(v).slice(0, 10).split('-');
   return d && m && y ? `${d}/${m}/${y}` : v;
}

// toYMD acepta YYYY-MM-DD, dd/mm/yyyy o un ISO con hora (Consultas manda la
// medianoche local en UTC: se toma la fecha local).
function toYMD(v) {
   if (!v) return null;
   const s = String(v);
   if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
      const [d, m, y] = s.split('/');
      return `${y}-${m}-${d}`;
   }
   if (s.includes('T')) {
      const dt = new Date(s);
      if (!Number.isNaN(dt.getTime())) {
         const pad = (n) => String(n).padStart(2, '0');
         return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
      }
   }
   return s.slice(0, 10);
}

async function getList(path, q) {
   const res = await api.get(path, q);
   if (!res.ok) throw new Error(res.message);
   return res.data || [];
}

// --- materiales ---

const kindTypeId = (kind) => (kind === 'tool' ? 2 : 1);
const typeKind = (typeId) => (Number(typeId) === 2 ? 'tool' : 'supply');
const typeName = (typeId) => (Number(typeId) === 2 ? 'Herramientas' : 'Insumos');

const materialToLegacy = (m) => ({
   id: m.id,
   code: m.code,
   name: m.name,
   type_of_material: { id: kindTypeId(m.kind), name: typeName(kindTypeId(m.kind)) },
   category_of_material: { id: m.category.id, name: m.category.name },
   comments: m.notes,
   id_status: statusId(m.active),
   status: statusName(m.active),
   quantity: m.quantity,
   max: m.max,
   min: m.min,
   price: m.price,
});

function materialBody(d) {
   const body = {
      code: d.code || '',
      name: d.name || '',
      categoryId: num(d.categoryId),
      notes: d.notes || '',
      active: isActive(d.idStatus, false),
      quantity: num(d.quantity),
      max: num(d.max),
      min: num(d.min),
   };
   if (d.price !== undefined && d.price !== null && d.price !== '') body.price = num(d.price);
   return body;
}

// --- personal ---

const staffToLegacy = (s) => ({
   id: s.id,
   name: s.name,
   start_date: toDMY(s.startDate),
   end_date: toDMY(s.endDate),
   id_status: statusId(s.active),
   status: statusName(s.active),
   region: { id: s.region.id, name: s.region.name },
   cost: s.cost,
   categories: s.categories || [],
});

const staffBody = (d) => ({
   name: d.name || '',
   startDate: toYMD(d.startDate),
   endDate: toYMD(d.endDate),
   active: isActive(d.idStatus),
   regionId: num(d.regionId),
   cost: num(d.cost),
   categoryIds: parse(d.categories).map(idOf).filter((id) => id > 0),
});

// --- categorías simples (personal y tareas) ---

const categoryToLegacy = (c) => ({ id: c.id, name: c.name, id_status: statusId(c.active), status: statusName(c.active) });

// --- tareas ---

const taskToLegacy = (t) => ({
   id: t.id,
   name: t.name,
   description: t.description,
   id_status: statusId(t.active),
   category: t.category || {},
   materials: t.materials || [],
   personal_categories: (t.staffCategories || []).map((c) => ({ ...c, id_status: 1 })),
   allowed_staff: t.allowedStaff || [],
});

const taskBody = (d) => ({
   name: d.name || '',
   description: d.description || '',
   categoryId: num(d.categoryId),
   active: isActive(d.idStatus),
   materialIds: parse(d.materials).map(idOf).filter((id) => id > 0),
   staffCategoryIds: parse(d.personalCategories).map(idOf).filter((id) => id > 0),
});

// --- proveedores ---

const providerToLegacy = (p) => ({
   id: p.id,
   name: p.name,
   comments: p.notes,
   statusId: statusId(p.active),
   Tasks: (p.tasks || []).map((t) => ({ id: t.id, name: t.name, category: t.category })),
});

const providerBody = (d) => ({
   name: d.Proveedor || '',
   notes: d.Observaciones || '',
   active: isActive(d.IdEstado),
   // Las tareas nuevas llegan con id 0.
   tasks: parse(d.Tasks).map((t) => ({ id: num(t.id), name: t.name || '', categoryId: idOf(t.category) })),
});

// --- compras ---

const purchaseToLegacy = (p) => ({
   id: p.id,
   invoice: p.invoice,
   date: toDMY(p.date),
   provider: p.provider,
   comments: p.notes,
   subtotal: p.subtotal,
   iva: p.tax,
   total: p.total,
   // getPurchases hace JSON.parse(materials): se manda como texto, igual que
   // la columna FOR JSON del SP.
   materials: JSON.stringify(
      (p.materials || []).map((m) => ({ id: m.materialId, code: m.code, name: m.name, quantity: m.quantity, price: m.price, amount: m.amount }))
   ),
});

// El subtotal y el total los calcula v1; solo se manda el IVA.
const purchaseBody = (d) => ({
   invoice: d.invoice || '',
   date: toYMD(d.date),
   provider: d.provider || '',
   notes: d.comments || '',
   tax: num(d.iva),
   materials: parse(d.materials).map((m) => ({ materialId: idOf(m), quantity: num(m.quantity), price: num(m.price) })),
});

// --- órdenes de servicio ---

const orderToLegacy = (o) => ({
   id: o.id,
   id_request: o.request.id,
   request: o.request.folio,
   unity: o.request.unit,
   project: o.request.project,
   description: o.description,
   comments: o.notes,
   start_date: toDMY(o.startDate) || '',
   end_date: toDMY(o.endDate) || '',
   tasks: o.tasks || [],
   type_of_service: o.serviceType,
   provider: o.provider,
   status: o.status,
   type_of_task: (o.tasks || [])[0] || null,
   staff: o.staff || [],
   materials: (o.materials || []).map((m) => ({
      id: m.id,
      name: m.name,
      expectedQuantity: m.expectedQuantity,
      realQuantity: m.realQuantity,
      quantity: m.stock,
      max: m.max,
      min: m.min,
   })),
});

const orderBody = (d, requestId) => {
   const provider = d.IdProvider;
   return {
      requestId: num(requestId),
      description: d.Descripcion || '',
      notes: d.Comentarios || '',
      serviceTypeId: num(d.IdTipoServicio),
      providerId: provider === undefined || provider === null || provider === '' ? null : num(provider),
      statusId: num(d.IdEstado),
      taskIds: parse(d.Tarea).map(idOf).filter((id) => id > 0),
      staff: parse(d.Personal).map((s) => ({ staffId: idOf(s), scheduledHours: num(s.scheduledHours), realHours: num(s.realHours) })),
      materials: parse(d.Materiales).map((m) => ({
         materialId: idOf(m),
         expectedQuantity: num(m.expectedQuantity),
         realQuantity: num(m.realQuantity),
      })),
   };
};

async function orderOptions() {
   const res = await api.get('costs/service-orders/options');
   if (!res.ok) throw new Error(res.message);
   return res.data;
}

// --- KPIs ---

const kpiRange = (url) => {
   const q = query(url);
   return { from: toYMD(q.from), to: toYMD(q.to) };
};

// --- rutas ---

export default {
   // Materiales y herramientas
   'GET costos/materiales': async () => {
      const rows = (await getList('costs/materials')).map(materialToLegacy);
      return {
         materials: rows.filter((m) => m.type_of_material.id === 1),
         tools: rows.filter((m) => m.type_of_material.id === 2),
      };
   },
   'POST costos/materiales': async (d) =>
      done(
         await api.post(
            'costs/materials',
            materialBody({
               code: d.Codigo,
               name: d.Nombre,
               categoryId: d.IdCategoriaMaterial,
               notes: d.Comentarios,
               idStatus: d.IdEstado,
               quantity: d.Cantidad,
               max: d.Max,
               min: d.Min,
               price: d.Price,
            })
         )
      ),
   'PUT costos/materiales': async (_, { url }) => {
      const q = query(url);
      return done(
         await api.put(
            `costs/materials/${q.id}`,
            materialBody({
               code: q.code,
               name: q.name,
               categoryId: q.idCategory,
               notes: q.comments,
               idStatus: q.idStatus,
               quantity: q.quantity,
               max: q.max,
               min: q.min,
               price: q.price,
            })
         )
      );
   },
   'DELETE costos/materiales/:x': async (_, { params }) => done(await api.del(`costs/materials/${params[0]}`)),

   // Categorías de materiales. v1 solo lista las activas (igual que el SP) y
   // no permite cambiar el tipo ni el estado: la edición solo cambia el nombre.
   'GET costos/materiales/categorias': async () =>
      (await getList('costs/material-categories')).map((c) => ({
         id: c.id,
         name: c.name,
         id_status: 1,
         type_of_material: kindTypeId(c.kind),
      })),
   'POST costos/materiales/categorias': async (d) =>
      done(await api.post('costs/material-categories', { name: d.Nombre || '', kind: typeKind(d.IdTipoMaterial) })),
   'PUT costos/materiales/categorias': async (_, { url }) => {
      const q = query(url);
      const cats = await getList('costs/material-categories');
      const cur = cats.find((c) => c.id === num(q.id));
      if (!cur) return { error: true, message: 'Categoría no encontrada' };
      return done(await api.put(`costs/material-categories/${q.id}`, { name: q.name || cur.name, kind: cur.kind }));
   },
   'DELETE costos/materiales/categorias/:x': async (_, { params }) =>
      done(await api.del(`costs/material-categories/${params[0]}`)),

   // Personal
   'GET costos/personal': async () => (await getList('costs/staff')).map(staffToLegacy),
   'POST costos/personal': async (d) =>
      done(
         await api.post(
            'costs/staff',
            staffBody({
               name: d.Nombre,
               startDate: d.FechaInicio,
               endDate: d.FechaFin,
               idStatus: d.IdEstado,
               cost: d.Costo,
               regionId: d.IdRegion,
               categories: d.Categories,
            })
         )
      ),
   'PUT costos/personal': async (_, { url }) => {
      const q = query(url);
      return done(await api.put(`costs/staff/${q.id}`, staffBody({ ...q, regionId: q.idRegion })));
   },
   'DELETE costos/personal/:x': async (_, { params }) => done(await api.del(`costs/staff/${params[0]}`)),

   // Categorías de personal
   'GET costos/personal/categoria': async () => (await getList('costs/staff-categories')).map(categoryToLegacy),
   'POST costos/personal/categoria': async (d) =>
      done(await api.post('costs/staff-categories', { name: d.Nombre || '', active: isActive(d.IdEstado) })),
   'PUT costos/personal/categoria': async (_, { url }) => {
      const q = query(url);
      return done(await api.put(`costs/staff-categories/${q.IdCategoria}`, { name: q.Nombre || '', active: isActive(q.IdEstado) }));
   },
   'DELETE costos/personal/categoria/:x': async (_, { params }) =>
      done(await api.del(`costs/staff-categories/${params[0]}`)),

   'GET costos/personal/region': async () => getList('costs/regions'),

   // Categorías de tareas (estas pantallas sí revisan res.error)
   'GET costos/tareas/categoria': async () => (await getList('costs/task-categories')).map(categoryToLegacy),
   'POST costos/tareas/categoria': async (d) =>
      done(await api.post('costs/task-categories', { name: d.Nombre || '', active: isActive(d.IdEstado) })),
   'PATCH costos/tareas/categoria': async (d) =>
      done(await api.put(`costs/task-categories/${d.IdCategoria}`, { name: d.Nombre || '', active: isActive(d.IdEstado) })),
   'DELETE costos/tareas/categoria/:x': async (_, { params }) => done(await api.del(`costs/task-categories/${params[0]}`)),

   // Tareas internas
   'GET costos/tareas': async () => (await getList('costs/tasks')).map(taskToLegacy),
   'POST costos/tareas': async (d) =>
      done(
         await api.post(
            'costs/tasks',
            taskBody({
               name: d.Nombre,
               description: d.Descripcion,
               categoryId: d.IdCategoria,
               idStatus: d.IdEstado,
               materials: d.Materials,
               personalCategories: d.PersonalCategories,
            })
         )
      ),
   'PUT costos/tareas': async (_, { url }) => {
      const q = query(url);
      return done(await api.put(`costs/tasks/${q.id}`, taskBody(q)));
   },
   'DELETE costos/tareas/:x': async (_, { params }) => done(await api.del(`costs/tasks/${params[0]}`)),
   // Opciones del formulario de tareas: solo lo activo.
   'GET costos/tareas/detalles': async () => {
      const [materials, staffCats, taskCats] = await Promise.all([
         getList('costs/materials'),
         getList('costs/staff-categories'),
         getList('costs/task-categories'),
      ]);
      const ref = (x) => ({ id: x.id, name: x.name });
      return {
         materials: materials.filter((m) => m.active).map(ref),
         personal_categories: staffCats.filter((c) => c.active).map(ref),
         categories: taskCats.filter((c) => c.active).map(ref),
      };
   },
   // Tareas de un proveedor. Un proveedor dado de baja responde 404 en v1:
   // se regresa la lista vacía, como hacía el SP.
   'GET costos/tareas/proveedor/:x': async (_, { params }) => {
      const res = await api.get(`costs/providers/${params[0]}/tasks`);
      if (res.status === 404) return [];
      if (!res.ok) return fail(res);
      return res.data || [];
   },

   // Proveedores
   'GET costos/proveedores': async () => (await getList('costs/providers')).map(providerToLegacy),
   'POST costos/proveedores': async (d) => done(await api.post('costs/providers', providerBody(d))),
   'PUT costos/proveedores': async (_, { url }) => {
      const q = query(url);
      return done(await api.put(`costs/providers/${q.IdProveedor}`, providerBody(q)));
   },
   'DELETE costos/proveedores/:x': async (_, { params }) => done(await api.del(`costs/providers/${params[0]}`)),

   // Compras
   'GET costos/compras': async () => (await getList('costs/purchases')).map(purchaseToLegacy),
   'POST costos/compras': async (d) =>
      done(
         await api.post(
            'costs/purchases',
            purchaseBody({ invoice: d.Folio, date: d.Fecha, provider: d.Proveedor, comments: d.Comentarios, iva: d.Iva, materials: d.Materials })
         )
      ),
   'PUT costos/compras': async (_, { url }) => {
      const q = query(url);
      return done(await api.put(`costs/purchases/${q.id}`, purchaseBody(q)));
   },
   'DELETE costos/compras/:x': async (_, { params }) => done(await api.del(`costs/purchases/${params[0]}`)),

   // Órdenes de servicio
   'GET costos/ordenes': async () => (await getList('costs/service-orders')).map(orderToLegacy),
   'POST costos/ordenes': async (d) => done(await api.post('costs/service-orders', orderBody(d, d.IdSolicitud))),
   // La edición vieja no manda la solicitud; v1 la pide (y no deja cambiarla),
   // así que se toma de la orden actual.
   'PATCH costos/ordenes': async (d) => {
      const orders = await getList('costs/service-orders');
      const cur = orders.find((o) => o.id === num(d.IdOs));
      if (!cur) return { error: true, message: 'Orden de servicio no encontrada' };
      return done(await api.put(`costs/service-orders/${d.IdOs}`, orderBody(d, cur.request.id)));
   },
   'DELETE costos/ordenes/:x': async (_, { params }) => done(await api.del(`costs/service-orders/${params[0]}`)),
   'GET costos/ordenes/detalles': async () => {
      const o = await orderOptions();
      return {
         status_list: o.statuses || [],
         types_of_service: o.serviceTypes || [],
         tasks: (o.tasks || []).map((t) => ({ id: t.id, name: t.name, allowed_staff: t.allowedStaff || [] })),
         requests: o.requests || [],
         providers: o.providers || [],
         materials: o.materials || [],
      };
   },
   'GET costos/ordenes/filtros': async () => (await orderOptions()).statuses || [],

   // KPIs. El proyecto de la ruta (:x) se ignora: el SP tampoco lo usa.
   'GET costos/kpi/:x': async (_, { url }) => {
      const rows = await getList('costs/kpis', kpiRange(url));
      return rows.map((k) => ({ orden: k.order, KPI: k.code, Nombre: k.name, Cantidad: k.value }));
   },
   // El detalle ya viene sin la fila de resumen (orden -1), como lo filtraba Koa.
   'GET costos/kpi/:x/:x': async (_, { params, url }) => {
      const res = await api.get(`costs/kpis/${encodeURIComponent(params[1])}`, kpiRange(url));
      if (res.status === 404) return [];
      if (!res.ok) return fail(res);
      return ((res.data && res.data.items) || []).map((i) => ({ orden: i.order, name: i.name, value: i.value }));
   },
};
